import React, { useEffect } from 'react';

import _ from 'lodash';
import { Box, Button, Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import OhsModal from 'global-components/modal/OhsModal';
import { OhsFormLayoutType, OhsFormSettings } from 'global-components/form/services/OhsFormModels';
import { OhsSearchInputFields } from 'search/OhsSearchModels';
import OhsModuleSearchForm from 'search/OhsModuleSearchForm';
import { ParamTypes } from 'global-services/register/OhsRegisterModels';
import { DateDueKey } from 'global-services/register/DateDueModels';
import { ModuleType } from 'global-services/constants/OhsObject';

import ohsDebounce from '../global-services/utils/OhsDebounce';

interface Props {
  inputFields: OhsSearchInputFields;
  open: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onModalClose: () => void;
  onSearch: (payload?: any) => void;
  resetFilters: () => void;
  searchFilter: any;
  moduleType?: ModuleType;
  withFilter?: boolean;
}

const useStyles = makeStyles()((theme: Theme) => ({
  filterActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '5px',
    width: '100%',
    '& button': {
      textTransform: 'none',
      minWidth: '100px',
      fontSize: '14px',
      padding: '2px 20px',
    },
    [theme.breakpoints.down(500)]: {
      '& button': { width: '100%' },
    },
  },
  searchButton: {
    marginLeft: 'auto',
  },
}));

function OhsModuleSearchModal(props: Props) {
  const { classes } = useStyles();
  const ohsFormSettings = new OhsFormSettings({}, OhsFormLayoutType.AngularModalForm);

  const initialForm = () => {
    const newForm = { ...props.searchFilter };

    const stringifyIfObject = (key: ParamTypes) => {
      newForm[key] = typeof newForm[key] === 'object' ? JSON.stringify(newForm[key]) : newForm[key];
    };

    [ParamTypes.Sort].forEach(stringifyIfObject);

    if (newForm.filter && newForm.filter[ParamTypes.DateDue]) {
      if ((props.inputFields?.dateDueOptions || [])?.length > 0) {
        newForm.filter[ParamTypes.DateDueType] = (props.inputFields?.dateDueOptions || []).some(
          (item) => item.value === newForm.filter[ParamTypes.DateDue]
        )
          ? newForm.filter[ParamTypes.DateDue]
          : DateDueKey.Custom;
      } else {
        // default as custom when without options
        newForm[ParamTypes.DateDueType] = DateDueKey.Custom;
      }
    }
    if (_.isUndefined(newForm.substringSearch)) newForm.substringSearch = true;
    return newForm;
  };

  const initializeForm = () => {
    ohsFormSettings.useFormMethods.reset(initialForm());
  };

  const resetAll = () => {
    props.resetFilters();
    props.onModalClose();
  };

  const resetSearch = () => {
    const initForm = initialForm();
    const payload = {
      ...initForm,
      [ParamTypes.SearchKey]: '',
      [ParamTypes.SubstringSearch]: undefined,
      filter: {
        ...initForm.filter,
        [ParamTypes.Page]: 1,
        [ParamTypes.Next]: undefined,
      },
    };
    props.onSearch(payload);
    props.onModalClose();
  };

  const formSubmit = (data: any) => {
    const payload = {
      ...data,
      filter: {
        ...data.filter,
        [ParamTypes.Page]: 1,
        [ParamTypes.Next]: undefined,
      },
    };
    props.onSearch(payload);
    props.onModalClose();
  };

  const state = ohsFormSettings.useFormMethods.formState.defaultValues;

  // trigger clear search every time the url changes
  useEffect(() => {
    if (props.open) initializeForm();
  }, [props.open]);

  // Autofocus
  useEffect(() => {
    const searchKeyState = ohsFormSettings.useFormMethods.getFieldState('searchKey');
    if (!searchKeyState.isTouched) ohsFormSettings.useFormMethods.setFocus('searchKey');
  }, [state]);

  return (
    <OhsModal
      title="Search"
      open={props.open}
      setModalOpenFunc={props.setModalOpen}
      onClose={props.onModalClose}
      actionNode={
        <Box className={classes.filterActions}>
          {props.withFilter ? (
            <>
              <Button className="ui gray button" onClick={resetAll} variant="contained">
                Reset All
              </Button>
              <Button className="ui orange button" onClick={resetSearch} variant="contained">
                Reset Search
              </Button>
            </>
          ) : (
            <Button className="ui gray button" onClick={resetAll} variant="contained">
              Reset
            </Button>
          )}

          <Box className={classes.searchButton}>
            <Button
              onClick={ohsDebounce(ohsFormSettings.useFormMethods.handleSubmit(formSubmit))}
              className="ui primary button"
              variant="contained"
              type="submit"
            >
              Search
            </Button>
          </Box>
        </Box>
      }
      ContentNode={
        <OhsModuleSearchForm
          ohsFormSettings={ohsFormSettings}
          formSubmit={formSubmit}
          inputFields={props.inputFields}
          moduleType={props.moduleType}
        />
      }
    />
  );
}
OhsModuleSearchModal.defaultProps = {
  moduleType: undefined,
  withFilter: true,
};

export default OhsModuleSearchModal;
